/**
 * Admin role → nav-group visibility mapping.
 *
 * Single source of truth for which admin roles can see which left-nav items.
 * Items are organised into hierarchical groups (group header + children).
 * To add a tab or change a role's access, edit `ADMIN_NAV_GROUPS` below.
 *
 * Roles are additive — an admin with multiple roles sees the union of their
 * tabs. `super-admin` is composed in Layout.tsx (Platform group prefix +
 * all admin groups).
 */

import type { NavItem } from '@shared-ui/sidebar';

/**
 * Admin nav-tab roles. Mirrors backend's `UserRole` (admin subset). The full
 * `UserRole` type also includes `'student'`, `'teacher'`, `'super_admin'`
 * — none of which are admin-tab roles, so they're excluded here.
 */
export const ADMIN_ROLES = [
  'content_admin',
  'prompt_admin',
  'eval_admin',
  'user_admin',
  'ops_admin',
  'tenant_admin',
] as const;

/** Roles only ever held at the `'platform'` scope. */
export const PLATFORM_ROLES = ['prompt_admin', 'eval_admin', 'ops_admin'] as const;

/** Roles held at a tenant (including the public tenant `'0'`). */
export const TENANT_ROLES = ['tenant_admin', 'user_admin', 'content_admin'] as const;

export function isPlatformRole(role: string): boolean {
  return (PLATFORM_ROLES as readonly string[]).includes(role);
}

/** The system tenant, where platform roles are held. */
export const PLATFORM_TENANT_ID = 'platform';

/**
 * Does this admin operate the product across tenants? The super-admin, or
 * anyone holding a role at the platform scope — the client-side twin of the
 * backend's `hasAnyPlatformRole`, which gates the `auth: 'platform'` tier.
 * Such an admin sees every tenant's people on Operations and the like, so
 * they get the tenant filter to narrow that view.
 */
export function operatesAcrossTenants(user: {
  isSuperAdmin?: boolean;
  roleAssignments?: { role: string; scope: string }[];
} | null | undefined): boolean {
  if (!user) return false;
  if (user.isSuperAdmin) return true;
  return (user.roleAssignments ?? []).some(a => a.scope === PLATFORM_TENANT_ID);
}

export type AdminRole = typeof ADMIN_ROLES[number];

/**
 * A single admin nav leaf — either a top-level item (Dashboard) or a child of
 * a group (Questions inside Content). `roles` controls visibility.
 */
export interface AdminNavLeaf {
  path: string;
  label: string;
  icon?: string;
  end?: boolean;
  roles: AdminRole[];
  /** True if the page renders tenant-scoped data and a super-admin must
   *  pick a tenant in TenantFilter before opening it. */
  requiresTenantForSuperAdmin?: boolean;
}

/** A group header + children. The group is shown if any child is visible. */
export interface AdminNavGroup {
  label: string;
  icon?: string;
  children: AdminNavLeaf[];
}

/**
 * `AdminNavEntry` is either a leaf shown directly at the top level, or a
 * group with children.
 */
export type AdminNavEntry = AdminNavLeaf | AdminNavGroup;

function isGroup(entry: AdminNavEntry): entry is AdminNavGroup {
  return 'children' in entry;
}

export const ADMIN_NAV_GROUPS: AdminNavEntry[] = [
  { path: '/', label: 'Dashboard', icon: '⌂', end: true, roles: ['tenant_admin'], requiresTenantForSuperAdmin: true },

  {
    label: 'Users',
    icon: '\u{1F465}',
    children: [
      { path: '/users',          label: 'Users',          roles: ['user_admin', 'tenant_admin'], requiresTenantForSuperAdmin: true },
      // Super-admin only, and reached from the Platform group — the Users tab
      // already lists admins via its own filter, so a second leaf was redundant.
      //
      // The entry STAYS here with an empty `roles` on purpose: `canAccessPath`
      // returns `true` for paths it does not know, so deleting the row would
      // reopen the hole where every admin role could reach /tenant-admins by
      // URL. Empty roles means "no admin role grants this" — visible to nobody,
      // denied to everyone, with super-admin bypassing in RoleGuard.
      { path: '/tenant-admins',  label: 'Admins',         roles: [] },
      { path: '/user-feedback',  label: 'User Feedback',  roles: ['user_admin', 'tenant_admin'] },
      { path: '/help-queries',   label: 'Student Doubts', roles: ['user_admin', 'tenant_admin'] },
      // Corrections, re-evals, overrides, disputes and clean-ups on students'
      // readings — student data, so the same roles as the rest of this group.
      { path: '/adjustments',    label: 'Adjustments',    roles: ['user_admin', 'tenant_admin'] },
    ],
  },

  {
    label: 'Content',
    icon: '\u{1F4DA}',
    children: [
      { path: '/questions',        label: 'Questions',        roles: ['content_admin', 'tenant_admin'] },
      { path: '/tests',            label: 'Question Sets',    roles: ['content_admin', 'tenant_admin'] },
      { path: '/whats-new',        label: "What's New / Tips", roles: ['content_admin', 'tenant_admin'] },
      { path: '/bookmarks',        label: 'Bookmarks',        roles: ['content_admin', 'tenant_admin'] },
      // Global content library — a tenant admin may browse it (the backend
      // serves read-only: they own none of it), but not author it.
      //
      // Modules, curricula, concepts and books are tabs of one page: four
      // entries for four views of "what we teach" pushed the rest of Content
      // off the fold. `roles` is the union of what those pages allowed —
      // Concepts stays content_admin-only, withheld as a tab inside the page
      // rather than by hiding the whole entry.
      { path: '/modules',          label: 'Modules & Curriculum', roles: ['content_admin', 'tenant_admin'] },
      // A directory of real schools — reference data about the world, curated by
      // us, like curricula. Browsable by a tenant admin; authored by content admins.
      { path: '/schools',          label: 'Schools',          roles: ['content_admin', 'tenant_admin'] },
      { path: '/site-usage',       label: 'Site Usage',       roles: ['content_admin'] },
      // Every URL we publish, and what each is doing. Platform content: a
      // tenant admin may not author one (the backend refuses it either way).
      { path: '/published-links',  label: 'Published Links',  roles: ['content_admin'] },
      { path: '/simulations',      label: 'Simulations',      roles: ['content_admin', 'tenant_admin'] },
      // Questions set up on a bench and checked against their key — catalogue
      // curation, so content_admin only. See question-scenes-target.md §10.
      { path: '/scenes',           label: 'Scenes',           roles: ['content_admin'] },
      // Study Plans is no longer its own entry: it is a tab of Modules &
      // Curriculum, beside Books. Plans we ship are content bound to a
      // CURRICULUM — most students are in no class, and a platform plan is the
      // only plan they have to take, so it belongs where its syllabus is
      // authored. The tab is withheld from tenant admins there, the way the
      // content_admin-only entry here was.
      // `/study-plans` still resolves: it redirects into that tab.
      // Platform content authoring — no tenant dimension, so no tenant_admin.
      { path: '/programs',         label: 'Programs',         roles: ['content_admin'] },
      { path: '/paper-imports',    label: 'Imports',          roles: ['content_admin'] },
      // Patterns sits directly above Moments because they read as a pair, but
      // they are NOT the same system: Patterns is emergent (produced during
      // solution analysis), Moments is a curated catalogue authored here.
      // Adjacent in the sidebar, separate everywhere else — including roles.
      { path: '/sticking-patterns', label: 'Sticking Patterns', roles: ['user_admin', 'tenant_admin'] },
      // Problem Types is a tab inside Learning Moments — both describe the
      // shapes of difficulty we recognise and what we say about them.
      { path: '/learning-moments', label: 'Learning Moments', roles: ['content_admin'] },
    ],
  },

  // Our own marketing content: platform content, like published links, so a
  // tenant admin does not see it. Content holds every series and episode; a
  // platform node is that content arranged for the platform, and is added only
  // once it has something of its own to show.
  // docs/FeatureDesigns/marketing/marketing-console-target.md
  {
    label: 'Marketing',
    icon: '\u{1F4E3}',
    children: [
      { path: '/marketing/content',   label: 'Content',   roles: ['content_admin'] },
      { path: '/marketing/instagram', label: 'Instagram', roles: ['content_admin'] },
      { path: '/marketing/youtube',   label: 'YouTube',   roles: ['content_admin'] },
    ],
  },

  // Platform roles only. Prompts, evals and model runs have no tenant
  // dimension, and Operations carries cross-tenant student submissions.
  {
    label: 'Prompts & Evals',
    icon: '✎',
    children: [
      { path: '/prompts',        label: 'Prompts & Pipelines', roles: ['prompt_admin'] },
      { path: '/submissions',    label: 'Operations',          roles: ['prompt_admin', 'eval_admin'] },
      { path: '/model-data',     label: 'Model Runs',          roles: ['prompt_admin', 'eval_admin'] },
      // Every question set in the system, tests and assignments, whoever made
      // them. Content → Question Sets is the admin's own authoring library;
      // this is the operational view over everybody's, and the entry point to a
      // paper's sittings and reports.
      { path: '/assignments-tests', label: 'Assignments & Tests', roles: ['prompt_admin', 'eval_admin'] },
      { path: '/evals',          label: 'Evals',               roles: ['eval_admin'] },
    ],
  },

  {
    label: 'System',
    icon: '⚙',
    children: [
      { path: '/settings',       label: 'Settings',       roles: ['ops_admin'] },
      { path: '/monitoring',     label: 'Monitoring',     roles: ['ops_admin'] },
      { path: '/user-actions',   label: 'Session Traces', roles: ['ops_admin'] },
      { path: '/persona-agents', label: 'Persona Agents', roles: ['eval_admin'] },
    ],
  },
];

/**
 * Role names an actor effectively holds, from their scoped assignments.
 *
 * Nav is coarse by design: it answers "may you open this page at all", while
 * the backend decides *whose* data you see once you are there. So a
 * `content_admin@0` and a `content_admin@acme` see the same Questions tab and
 * different questions inside it.
 *
 * Platform roles can only ever be held at `'platform'` (the grant path enforces
 * it), so a role name alone is enough to gate the platform-only tabs.
 *
 * Falls back to the legacy `roles[]` for admins not yet backfilled — the same
 * dual-read the backend applies. See
 * docs/FeatureDesigns/multi-tenancy/roles-and-scopes.md.
 */
/** The public (direct-to-consumer) tenant. Mirrors TENANT_ID_PUBLIC. */
export const PUBLIC_TENANT = '0';

/**
 * Does this actor administer the PUBLIC tenant?
 *
 * The question a platform-wide credential asks. One WABA sends every tenant's
 * OTP and one ad account pays for every tenant's traffic, so those settings
 * belong to tenant `0` — a school's `tenant_admin@acme` holds authority over
 * its own tenant and none over these.
 *
 * Scope matters here, unlike the nav config above, which asks only "may you
 * open this page at all". Mirrors `assertCanAdministerPlatformChannel` on the
 * backend, which is what actually enforces it; this decides what to show.
 */
export function administersPublicTenant(user: {
  isSuperAdmin?: boolean;
  roleAssignments?: readonly { role: string; scope: string }[];
} | undefined | null): boolean {
  if (!user) return false;
  if (user.isSuperAdmin) return true;
  return (user.roleAssignments ?? []).some(
    a => a.role === 'tenant_admin' && a.scope === PUBLIC_TENANT,
  );
}

export function effectiveRoleNames(user: {
  roles?: readonly string[];
  roleAssignments?: readonly { role: string; scope: string }[];
} | undefined | null): string[] {
  if (!user) return [];
  const assignments = user.roleAssignments ?? [];
  if (assignments.length > 0) return [...new Set(assignments.map(a => a.role))];
  return [...(user.roles ?? [])];
}

/**
 * Convert nav config into the `NavItem[]` shape the Sidebar expects, filtered
 * by visibility. Empty groups are dropped. Children are filtered per their
 * `roles`. The `decorate` hook lets callers attach badges or other per-leaf
 * data (e.g. unread feedback count) without re-walking the tree.
 */
export function visibleNavItemsForRoles(
  roles: readonly string[] | undefined,
  decorate?: (leaf: AdminNavLeaf) => Partial<NavItem>,
): NavItem[] {
  if (!roles || roles.length === 0) return [];
  const set = new Set<string>(roles);
  const visibleLeaf = (leaf: AdminNavLeaf): boolean => leaf.roles.some(r => set.has(r));
  const toNavItem = (leaf: AdminNavLeaf): NavItem => ({
    path: leaf.path,
    label: leaf.label,
    icon: leaf.icon,
    end: leaf.end,
    ...(decorate?.(leaf) ?? {}),
  });

  const out: NavItem[] = [];
  for (const entry of ADMIN_NAV_GROUPS) {
    if (isGroup(entry)) {
      const visibleChildren = entry.children.filter(visibleLeaf).map(toNavItem);
      if (visibleChildren.length === 0) continue;
      out.push({ label: entry.label, icon: entry.icon, children: visibleChildren });
    } else if (visibleLeaf(entry)) {
      out.push(toNavItem(entry));
    }
  }
  return out;
}

/**
 * The first navigable leaf path for the given roles, used as the post-login
 * landing for non-super-admin users who can't see the Dashboard. Walks the
 * top-level entries in declared order and returns the first matching leaf
 * (either a top-level leaf, or the first visible child of a group). Returns
 * undefined when nothing in the nav config matches.
 */
export function firstVisibleLeafPath(roles: readonly string[] | undefined): string | undefined {
  if (!roles || roles.length === 0) return undefined;
  const set = new Set<string>(roles);
  const visibleLeaf = (leaf: AdminNavLeaf) => leaf.roles.some(r => set.has(r));
  for (const entry of ADMIN_NAV_GROUPS) {
    if (isGroup(entry)) {
      const child = entry.children.find(visibleLeaf);
      if (child) return child.path;
    } else if (visibleLeaf(entry)) {
      return entry.path;
    }
  }
  return undefined;
}

/**
 * True if the given route path is tenant-scoped — super-admin must pick a
 * tenant in TenantFilter before opening it. Detail routes (e.g. /users/:id)
 * inherit from their parent tab — pass the parent path here.
 */
export function tabRequiresTenantForSuperAdmin(path: string): boolean {
  for (const entry of ADMIN_NAV_GROUPS) {
    if (isGroup(entry)) {
      const child = entry.children.find(c => c.path === path);
      if (child) return child.requiresTenantForSuperAdmin === true;
    } else if (entry.path === path) {
      return entry.requiresTenantForSuperAdmin === true;
    }
  }
  return false;
}

/**
 * Whether any of the given roles is allowed to access the given route path.
 * Routes not present in the nav config are treated as accessible (e.g. detail
 * routes like /users/:id whose access is governed by the parent tab).
 *
 * Pass the user's full `roles[]` directly — non-admin values
 * (`student`/`teacher`/`super_admin`) won't match any tab's allow-list and
 * are harmlessly ignored.
 */
/** Who may review lab reactions and author guided experiments. */
export const CHEMISTRY_CURATION_ROLES: readonly AdminRole[] = ['content_admin'];

export function canCurateChemistry(user: {
  isSuperAdmin?: boolean;
  roles?: readonly string[];
  roleAssignments?: readonly { role: string; scope: string }[];
} | undefined | null): boolean {
  if (!user) return false;
  if (user.isSuperAdmin) return true;
  const roles = new Set(effectiveRoleNames(user));
  return CHEMISTRY_CURATION_ROLES.some(r => roles.has(r));
}

export function canAccessPath(path: string, roles: readonly string[]): boolean {
  const set = new Set<string>(roles);
  for (const entry of ADMIN_NAV_GROUPS) {
    if (isGroup(entry)) {
      const child = entry.children.find(c => c.path === path);
      if (child) return child.roles.some(r => set.has(r));
    } else if (entry.path === path) {
      return entry.roles.some(r => set.has(r));
    }
  }
  return true;
}
